import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { bannerAPI } from '../../services/api';

interface BannerMedia {
  image: string;
  video: string | null;
}

interface BannerLink {
  product: any;
  category: any;
  link: string | null;
  productId: string | null;
  productname: string | null;
  categoryId: string | null;
  categoryname: string | null;
}

interface Banner {
  id: number;
  media: BannerMedia[];
  link: BannerLink[];
}

interface BannerState {
  banners: Banner[];
  loading: boolean;
  error: string | null;
}

const initialState: BannerState = {
  banners: [],
  loading: false,
  error: null,
};

// Real API calls
export const fetchBanners = createAsyncThunk(
  'banner/fetchBanners',
  async () => {
    const response = await bannerAPI.getBanners();
    return response.data;
  }
);

export const addBanner = createAsyncThunk(
  'banner/addBanner',
  async (bannerData: any) => {
    const response = await bannerAPI.addBanner(bannerData);
    return response.data;
  }
);

export const updateBanner = createAsyncThunk(
  'banner/updateBanner',
  async ({ id, bannerData }: { id: string; bannerData: any }) => {
    const response = await bannerAPI.updateBanner(id, bannerData);
    return response.data;
  }
);

export const deleteBanner = createAsyncThunk(
  'banner/deleteBanner',
  async (id: string) => {
    await bannerAPI.deleteBanner(id);
    return id;
  }
);

const bannerSlice = createSlice({
  name: 'banner',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch banners
      .addCase(fetchBanners.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBanners.fulfilled, (state, action) => {
        state.loading = false;
        state.banners = action.payload.banners || [];
      })
      .addCase(fetchBanners.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch banners';
      })
      // Add banner
      .addCase(addBanner.fulfilled, (state, action) => {
        state.banners.unshift(action.payload);
      })
      // Update banner
      .addCase(updateBanner.fulfilled, (state, action) => {
        const index = state.banners.findIndex(banner => banner.id === action.payload.id);
        if (index !== -1) {
          state.banners[index] = action.payload;
        }
      })
      // Delete banner
      .addCase(deleteBanner.fulfilled, (state, action) => {
        state.banners = state.banners.filter(banner => banner.id !== parseInt(action.payload));
      });
  },
});

export const { clearError } = bannerSlice.actions;
export default bannerSlice.reducer; 